import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { dialogContentOutsideGuards } from "../../utils/dialogOutsideGuards";
import {
  Megaphone, Plus, Mail, MessageSquare, Users, Sparkles, Send, Pause, Play,
  Trash2, Loader2, BarChart3, Inbox, X, Eye, Wand2, ChevronRight,
  TrendingUp, MousePointerClick, Reply, DollarSign, Target,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";

const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const AUDIENCE_OPTIONS = [
  { value: "all_customers", label: "All Customers" },
  { value: "completed_jobs", label: "Customers with Completed Jobs" },
  { value: "unapproved_estimates", label: "Customers with Pending Estimates" },
  { value: "unpaid_invoices", label: "Customers with Unpaid Invoices" },
  { value: "inactive", label: "Inactive Customers (haven't booked in X days)" },
];

const TRIGGER_OPTIONS = [
  { value: "manual", label: "Manual Launch" },
  { value: "after_estimate_sent", label: "After Estimate Sent" },
  { value: "after_job_completed", label: "After Job Completed" },
  { value: "invoice_unpaid_x_days", label: "Invoice Unpaid X Days" },
  { value: "customer_inactive_x_days", label: "Customer Inactive X Days" },
];

const CHANNEL_OPTIONS = [
  { value: "email", label: "Email Only" },
  { value: "sms", label: "SMS Only" },
  { value: "both", label: "Email + SMS" },
];

const PERSONALIZATION_VARS = [
  "customer_name", "business_name", "business_phone", "technician_name",
  "service_type", "job_address", "estimate_link", "invoice_link",
  "review_link", "booking_link",
];

const STATUS_PILL = {
  draft: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  paused: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  completed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
};

// ─── Empty state ───────────────────────────────────────────────────

const EmptyState = ({ onCreate }) => (
  <Card className="glass-card border-theme" data-testid="campaigns-empty-state">
    <CardContent className="py-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-500/15 flex items-center justify-center">
        <Megaphone className="w-7 h-7 text-amber-500" />
      </div>
      <h3 className="text-foreground text-base font-semibold font-['Manrope']">No campaigns yet</h3>
      <p className="text-muted-foreground text-sm mt-1.5 max-w-md mx-auto">
        Launch your first marketing campaign — promote services, follow up on estimates,
        request reviews, or re-engage inactive customers.
      </p>
      <Button
        className="mt-5 btn-primary"
        onClick={onCreate}
        data-testid="empty-create-campaign-btn"
      >
        <Plus className="w-4 h-4 mr-2" /> Create Campaign
      </Button>
    </CardContent>
  </Card>
);

// ─── Campaign card ─────────────────────────────────────────────────

const CampaignCard = ({ c, onLaunch, onPause, onResume, onEdit, onLogs, onDelete, busy }) => {
  const Channel = c.channel === "email" ? Mail : c.channel === "sms" ? MessageSquare : Inbox;
  const audienceLabel = AUDIENCE_OPTIONS.find((a) => a.value === c.audience_type)?.label || c.audience_type;
  return (
    <Card className="glass-card border-theme" data-testid={`campaign-card-${c.campaign_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-foreground font-semibold text-sm font-['Manrope'] truncate">{c.name}</h3>
              <Badge className={`text-[10px] border ${STATUS_PILL[c.status] || STATUS_PILL.draft}`}>
                {c.status}
              </Badge>
              <Badge variant="outline" className="text-[10px] gap-1 capitalize">
                <Channel className="w-3 h-3" /> {c.channel}
              </Badge>
            </div>
            {c.description && (
              <p className="text-muted-foreground text-xs mt-1 line-clamp-2">{c.description}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {audienceLabel}</span>
              <span className="inline-flex items-center gap-1"><Send className="w-3 h-3" /> Sent: {c.stats?.sent || 0}</span>
              {c.stats?.failed > 0 && <span className="inline-flex items-center gap-1 text-red-400">Failed: {c.stats.failed}</span>}
              {c.stats?.skipped > 0 && <span>Skipped: {c.stats.skipped}</span>}
              {c.last_run_at && (
                <span>Last run: {new Date(c.last_run_at).toLocaleString("en-US", {month: "short", day: "numeric", hour: "numeric", minute: "2-digit"})}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onLaunch(c)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`campaign-launch-${c.campaign_id}`}
            title="Send to all matching customers now"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Send className="w-3 h-3 mr-1.5" />}
            Launch
          </Button>
          {c.status === "active" ? (
            <Button size="sm" variant="outline" onClick={() => onPause(c)} data-testid={`campaign-pause-${c.campaign_id}`}>
              <Pause className="w-3 h-3 mr-1.5" /> Pause
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onResume(c)} data-testid={`campaign-resume-${c.campaign_id}`}>
              <Play className="w-3 h-3 mr-1.5" /> Activate
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onLogs(c)} data-testid={`campaign-logs-${c.campaign_id}`}>
            <BarChart3 className="w-3 h-3 mr-1.5" /> View Logs
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`campaign-edit-${c.campaign_id}`}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(c)}
            data-testid={`campaign-delete-${c.campaign_id}`}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Builder dialog ────────────────────────────────────────────────

// One step's default. Step 1 always has delay=0 (sends immediately on launch).
const blankStep = (overrides = {}) => ({
  channel: "email",
  delay_value: 0,
  delay_unit: "minutes",
  subject: "",
  email_body: "",
  sms_body: "",
  is_enabled: true,
  ...overrides,
});

const DELAY_UNITS = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

const initialForm = {
  name: "", description: "",
  audience_type: "all_customers", audience_params: {},
  trigger_type: "manual", trigger_params: {},
  steps: [blankStep()],
  template_id: null,
  respect_opt_out: true,
  business_hours_only: false,
  is_active: false,
};

const CampaignBuilder = ({ open, onClose, templates, onSaved, editing }) => {
  const [form, setForm] = useState(initialForm);
  const [preview, setPreview] = useState({ total: 0, reachable_email: 0, reachable_sms: 0 });
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate when opening (new or edit)
  useEffect(() => {
    if (!open) return;
    if (editing) {
      // Backwards-compat: if the editing campaign has no `steps`, synthesize
      // a 1-step sequence from the legacy single-body fields.
      let steps = editing.steps && editing.steps.length > 0
        ? editing.steps.map((s) => ({
            channel: s.channel || "email",
            delay_value: s.delay_value ?? 0,
            delay_unit: s.delay_unit || "minutes",
            subject: s.subject || "",
            email_body: s.email_body || "",
            sms_body: s.sms_body || "",
            is_enabled: s.is_enabled !== false,
          }))
        : [blankStep({
            channel: editing.channel || "email",
            subject: editing.subject || "",
            email_body: editing.email_body || "",
            sms_body: editing.sms_body || "",
          })];
      setForm({
        name: editing.name || "",
        description: editing.description || "",
        audience_type: editing.audience_type || "all_customers",
        audience_params: editing.audience_params || {},
        trigger_type: editing.trigger_type || "manual",
        trigger_params: editing.trigger_params || {},
        steps,
        template_id: editing.template_id || null,
        respect_opt_out: editing.respect_opt_out !== false,
        business_hours_only: !!editing.business_hours_only,
        is_active: !!editing.is_active,
      });
    } else {
      setForm({ ...initialForm, steps: [blankStep()] });
    }
  }, [open, editing]);

  // Audience preview is keyed on channel of step 1 for a quick estimate
  const previewChannel = form.steps?.[0]?.channel || "email";

  useEffect(() => {
    if (!open) return;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/campaigns/preview-audience`, {
          name: form.name || "preview",
          audience_type: form.audience_type,
          audience_params: form.audience_params,
          trigger_type: form.trigger_type,
          channel: previewChannel,
        }, getAuth());
        setPreview(res.data);
      } catch {
        setPreview({ total: 0, reachable_email: 0, reachable_sms: 0 });
      } finally {
        setPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.audience_type, form.audience_params, previewChannel, form.trigger_type, form.name]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    // Templates fill Step 1 entirely. Subsequent steps (if any) are
    // preserved so users can build on top of a template.
    setForm((prev) => ({
      ...prev,
      template_id: tplId,
      name: prev.name || tpl.name,
      description: prev.description || tpl.description,
      audience_type: tpl.audience_type || prev.audience_type,
      audience_params: tpl.audience_params || {},
      steps: [
        blankStep({
          channel: tpl.channel,
          subject: tpl.subject || "",
          email_body: tpl.email_body || "",
          sms_body: tpl.sms_body || "",
        }),
        ...prev.steps.slice(1),
      ],
    }));
    toast.success(`Loaded template: ${tpl.name}`);
  };

  // ─── Step manipulation helpers ───────────────────────────────
  const updateStep = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };
  const addStep = () => {
    setForm((prev) => ({
      ...prev,
      // New steps default to 2 days later (most common follow-up cadence)
      steps: [...prev.steps, blankStep({ delay_value: 2, delay_unit: "days" })],
    }));
  };
  const removeStep = (idx) => {
    setForm((prev) => {
      if (prev.steps.length <= 1) return prev;  // always keep at least one
      return { ...prev, steps: prev.steps.filter((_, i) => i !== idx) };
    });
  };
  const moveStep = (idx, direction) => {
    setForm((prev) => {
      const newSteps = [...prev.steps];
      const j = idx + direction;
      if (j < 0 || j >= newSteps.length) return prev;
      [newSteps[idx], newSteps[j]] = [newSteps[j], newSteps[idx]];
      return { ...prev, steps: newSteps };
    });
  };
  const insertVarIntoStep = (idx, field, v) => {
    updateStep(idx, { [field]: `${form.steps[idx][field] || ""} {{${v}}}` });
  };

  const validate = () => {
    if (!form.name.trim()) return "Campaign name is required";
    if (!form.steps.length) return "At least one step is required";
    for (let i = 0; i < form.steps.length; i++) {
      const s = form.steps[i];
      if (!s.is_enabled) continue;  // disabled steps don't need content
      if (s.channel === "email" || s.channel === "both") {
        if (!s.subject?.trim()) return `Step ${i + 1}: email subject is required`;
        if (!s.email_body?.trim()) return `Step ${i + 1}: email body is required`;
      }
      if (s.channel === "sms" || s.channel === "both") {
        if (!s.sms_body?.trim()) return `Step ${i + 1}: SMS body is required`;
      }
      if (s.delay_value < 0) return `Step ${i + 1}: delay cannot be negative`;
    }
    return null;
  };

  const handleSave = async (saveAsDraft) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      // Send the full multi-step payload AND populate the legacy top-
      // level fields from Step 1 — keeps Phase-1-era clients happy.
      const step1 = form.steps[0];
      const payload = {
        ...form,
        is_active: !saveAsDraft && form.is_active,
        channel: step1.channel,
        subject: step1.subject,
        email_body: step1.email_body,
        sms_body: step1.sms_body,
      };
      if (editing) {
        const res = await axios.put(`${API}/campaigns/${editing.campaign_id}`, payload, getAuth());
        onSaved(res.data, false);
      } else {
        const res = await axios.post(`${API}/campaigns`, payload, getAuth());
        onSaved(res.data, true);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="campaign-builder-dialog"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-500" />
            {editing ? "Edit Campaign" : "Create Campaign"}
          </DialogTitle>
          <DialogDescription>
            Reach customers with a targeted email or SMS — pick a template or start from scratch.
          </DialogDescription>
        </DialogHeader>

        {/* Template picker */}
        {!editing && templates.length > 0 && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="w-4 h-4 text-amber-500" />
              <Label className="text-foreground text-sm font-medium">Start from a template</Label>
            </div>
            <Select value={form.template_id || ""} onValueChange={applyTemplate}>
              <SelectTrigger className="bg-theme-surface border-theme" data-testid="template-picker">
                <SelectValue placeholder="Pick a built-in template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="camp-name">Campaign Name</Label>
            <Input
              id="camp-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Q1 Tax Refund Promo"
              data-testid="campaign-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="camp-desc">Description <span className="text-[10px] text-muted-foreground/60">(internal)</span></Label>
            <Input
              id="camp-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="One-line summary for your team"
            />
          </div>
        </div>

        {/* Audience */}
        <div className="rounded-xl border border-theme bg-theme-surface p-3 space-y-2">
          <Label className="text-foreground text-sm font-medium flex items-center gap-1.5">
            <Users className="w-4 h-4 text-blue-400" /> Audience
          </Label>
          <Select value={form.audience_type} onValueChange={(v) => setForm({ ...form, audience_type: v })}>
            <SelectTrigger data-testid="campaign-audience-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUDIENCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.audience_type === "inactive" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Inactive for</Label>
              <Input
                type="number"
                min={1}
                value={form.audience_params.days ?? 90}
                onChange={(e) => setForm({ ...form, audience_params: { days: parseInt(e.target.value) || 90 } })}
                className="w-20 h-8"
                data-testid="inactive-days-input"
              />
              <span className="text-xs text-muted-foreground">days</span>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1 text-xs">
            {previewing ? (
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Counting…
              </span>
            ) : (
              <>
                <Badge variant="outline" data-testid="audience-preview-total">
                  <Eye className="w-3 h-3 mr-1" /> {preview.total} matching
                </Badge>
                {previewChannel !== "sms" && (
                  <Badge variant="outline" className="text-blue-400">
                    <Mail className="w-3 h-3 mr-1" /> {preview.reachable_email} reachable by email
                  </Badge>
                )}
                {previewChannel !== "email" && (
                  <Badge variant="outline" className="text-green-400">
                    <MessageSquare className="w-3 h-3 mr-1" /> {preview.reachable_sms} reachable by SMS
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>

        {/* Trigger */}
        <div className="space-y-1.5">
          <Label>Trigger</Label>
          <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}>
            <SelectTrigger data-testid="campaign-trigger-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* When the trigger is time-windowed, ask for the X-days parameter
              inline. Server reads this from `trigger_params.days`. */}
          {["after_estimate_sent", "invoice_unpaid_x_days", "customer_inactive_x_days"].includes(form.trigger_type) && (
            <div className="flex items-center gap-2 pt-1.5">
              <span className="text-[11px] text-muted-foreground/80 shrink-0">Fire after</span>
              <Input
                type="number"
                min={1}
                max={365}
                value={form.trigger_params?.days ?? (form.trigger_type === "customer_inactive_x_days" ? 90 : form.trigger_type === "invoice_unpaid_x_days" ? 7 : 3)}
                onChange={(e) => setForm({ ...form, trigger_params: { ...(form.trigger_params || {}), days: parseInt(e.target.value) || 1 } })}
                className="w-20"
                data-testid="campaign-trigger-days-input"
              />
              <span className="text-[11px] text-muted-foreground/80">
                day{(form.trigger_params?.days || 1) === 1 ? "" : "s"}
                {form.trigger_type === "after_estimate_sent" && " after the estimate was sent (if still pending)"}
                {form.trigger_type === "invoice_unpaid_x_days" && " after the invoice was sent (if still unpaid)"}
                {form.trigger_type === "customer_inactive_x_days" && " since the customer's last completed job"}
              </span>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground/70">
            Auto-triggers fire every hour via the scheduler. Each customer is queued at most once per trigger window.
          </p>
        </div>

        {/* Multi-step sequence editor */}
        <div className="rounded-xl border border-theme bg-theme-surface p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-foreground text-sm font-medium flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-amber-500" /> Sequence
              <span className="text-[10px] text-muted-foreground/70 font-normal">
                ({form.steps.length} step{form.steps.length === 1 ? "" : "s"})
              </span>
            </Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addStep}
              data-testid="campaign-add-step-btn"
            >
              <Plus className="w-3 h-3 mr-1.5" /> Add Step
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/70 -mt-1">
            Step 1 fires immediately on launch. Each subsequent step fires after its delay
            (relative to the previous step).
          </p>

          {form.steps.map((step, idx) => {
            const isEmail = step.channel === "email" || step.channel === "both";
            const isSms = step.channel === "sms" || step.channel === "both";
            return (
              <div
                key={idx}
                className="rounded-lg border border-theme bg-theme p-3 space-y-3"
                data-testid={`campaign-step-${idx}`}
              >
                {/* Step header */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-semibold">
                      Step {idx + 1}
                    </Badge>
                    <Switch
                      checked={step.is_enabled}
                      onCheckedChange={(v) => updateStep(idx, { is_enabled: v })}
                      data-testid={`step-${idx}-enabled-toggle`}
                    />
                    <span className="text-[11px] text-muted-foreground">
                      {step.is_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => moveStep(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`step-${idx}-move-up`}
                      title="Move up"
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => moveStep(idx, 1)}
                      disabled={idx === form.steps.length - 1}
                      data-testid={`step-${idx}-move-down`}
                      title="Move down"
                    >
                      ↓
                    </Button>
                    {form.steps.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => removeStep(idx)}
                        data-testid={`step-${idx}-remove`}
                        title="Remove step"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Delay + Channel */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      {idx === 0 ? "Delay (ignored — sends immediately)" : "Delay after previous step"}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={step.delay_value}
                        onChange={(e) => updateStep(idx, { delay_value: parseInt(e.target.value) || 0 })}
                        disabled={idx === 0}
                        className="w-24"
                        data-testid={`step-${idx}-delay-value`}
                      />
                      <Select
                        value={step.delay_unit}
                        onValueChange={(v) => updateStep(idx, { delay_unit: v })}
                        disabled={idx === 0}
                      >
                        <SelectTrigger className="flex-1" data-testid={`step-${idx}-delay-unit`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DELAY_UNITS.map((u) => (
                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Channel</Label>
                    <Select
                      value={step.channel}
                      onValueChange={(v) => updateStep(idx, { channel: v })}
                    >
                      <SelectTrigger data-testid={`step-${idx}-channel`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNEL_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Email content (conditional) */}
                {isEmail && (
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2">
                    <Label className="text-foreground text-xs font-medium flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-blue-400" /> Email
                    </Label>
                    <Input
                      value={step.subject || ""}
                      onChange={(e) => updateStep(idx, { subject: e.target.value })}
                      placeholder="Subject — Hi {{customer_name}}, …"
                      data-testid={`step-${idx}-subject`}
                    />
                    <Textarea
                      value={step.email_body || ""}
                      onChange={(e) => updateStep(idx, { email_body: e.target.value })}
                      rows={4}
                      placeholder={"Hi {{customer_name}},\n\n…"}
                      data-testid={`step-${idx}-email-body`}
                    />
                    <div className="flex flex-wrap gap-1">
                      {PERSONALIZATION_VARS.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => insertVarIntoStep(idx, "email_body", v)}
                          className="px-2 py-0.5 rounded text-[10px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 transition-colors"
                          data-testid={`step-${idx}-email-var-${v}`}
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* SMS content (conditional) */}
                {isSms && (
                  <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3 space-y-2">
                    <Label className="text-foreground text-xs font-medium flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-green-400" /> SMS
                    </Label>
                    <Textarea
                      value={step.sms_body || ""}
                      onChange={(e) => updateStep(idx, { sms_body: e.target.value })}
                      rows={3}
                      placeholder="Hi {{customer_name}}, … Reply STOP to opt out."
                      data-testid={`step-${idx}-sms-body`}
                    />
                    <div className="flex flex-wrap gap-1">
                      {PERSONALIZATION_VARS.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => insertVarIntoStep(idx, "sms_body", v)}
                          className="px-2 py-0.5 rounded text-[10px] font-mono text-green-400 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 transition-colors"
                          data-testid={`step-${idx}-sms-var-${v}`}
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60">
                      Tip: include "Reply STOP to opt out." for compliance.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Safety options */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-theme bg-theme-surface p-3">
          <div className="flex items-center gap-2 flex-1">
            <Switch
              checked={form.respect_opt_out}
              onCheckedChange={(v) => setForm({ ...form, respect_opt_out: v })}
              data-testid="respect-opt-out-toggle"
            />
            <div>
              <p className="text-sm text-foreground">Respect opt-outs</p>
              <p className="text-[10px] text-muted-foreground/70">Skip customers who replied STOP</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              data-testid="campaign-active-toggle"
            />
            <div>
              <p className="text-sm text-foreground">Activate on save</p>
              <p className="text-[10px] text-muted-foreground/70">Otherwise saved as draft</p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="campaign-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(true)}
            disabled={saving}
            data-testid="campaign-save-draft-btn"
          >
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn-primary"
            data-testid="campaign-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {form.is_active ? "Save & Activate" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Logs viewer (with Analytics tab) ─────────────────────────────

const LogsViewer = ({ open, onClose, campaign }) => {
  const [data, setData] = useState({ logs: [], recipients: [] });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("analytics");

  useEffect(() => {
    if (!open || !campaign) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/campaigns/${campaign.campaign_id}/logs`, getAuth())
        .then((r) => r.data)
        .catch(() => ({ logs: [], recipients: [] })),
      axios.get(`${API}/campaigns/${campaign.campaign_id}/analytics`, getAuth())
        .then((r) => r.data)
        .catch(() => null),
    ])
      .then(([logsData, analyticsData]) => {
        setData(logsData);
        setAnalytics(analyticsData);
      })
      .finally(() => setLoading(false));
  }, [open, campaign]);

  const fmtRevenue = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="campaign-logs-dialog"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">{campaign?.name || "Campaign"} — Analytics & Logs</DialogTitle>
          <DialogDescription>Funnel, per-step breakdown, delivery status, and event history.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b border-theme">
          <button
            className={`px-3 py-2 text-sm font-medium ${tab === "analytics" ? "text-foreground border-b-2 border-amber-500" : "text-muted-foreground"}`}
            onClick={() => setTab("analytics")}
            data-testid="logs-tab-analytics"
          >
            Analytics
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium ${tab === "recipients" ? "text-foreground border-b-2 border-amber-500" : "text-muted-foreground"}`}
            onClick={() => setTab("recipients")}
            data-testid="logs-tab-recipients"
          >
            Recipients ({data.recipients?.length || 0})
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium ${tab === "events" ? "text-foreground border-b-2 border-amber-500" : "text-muted-foreground"}`}
            onClick={() => setTab("events")}
            data-testid="logs-tab-events"
          >
            Events ({data.logs?.length || 0})
          </button>
        </div>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block text-muted-foreground" /></div>
        ) : tab === "analytics" ? (
          !analytics ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="campaign-analytics-empty">
              No analytics yet — launch the campaign to populate metrics.
            </p>
          ) : (
            <div className="space-y-4" data-testid="campaign-analytics-panel">
              {/* Rate badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Open</p>
                  <p className="text-lg font-bold text-blue-400 font-['Manrope']" data-testid="single-open-rate">
                    {analytics.rates.open_rate}%
                  </p>
                </div>
                <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Click</p>
                  <p className="text-lg font-bold text-purple-400 font-['Manrope']" data-testid="single-click-rate">
                    {analytics.rates.click_rate}%
                  </p>
                </div>
                <div className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Reply</p>
                  <p className="text-lg font-bold text-pink-400 font-['Manrope']" data-testid="single-reply-rate">
                    {analytics.rates.reply_rate}%
                  </p>
                </div>
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Conv.</p>
                  <p className="text-lg font-bold text-emerald-400 font-['Manrope']" data-testid="single-conv-rate">
                    {analytics.rates.conversion_rate}%
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {fmtRevenue(analytics.revenue)}
                  </p>
                </div>
              </div>

              {/* Funnel */}
              <div className="rounded-xl border border-theme bg-theme-surface p-3">
                <h4 className="text-foreground text-xs font-semibold mb-2 uppercase tracking-wider">
                  Funnel
                </h4>
                <FunnelChart funnel={analytics.funnel} />
                <p className="text-[10px] text-muted-foreground/70 mt-2">
                  Conversions = recipients who created a job within {analytics.attribution_days} days of send.
                </p>
              </div>

              {/* Per-step breakdown (only if multi-step) */}
              {analytics.steps && analytics.steps.length > 1 && (
                <div className="rounded-xl border border-theme bg-theme-surface p-3">
                  <h4 className="text-foreground text-xs font-semibold mb-2 uppercase tracking-wider">
                    Per Step
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="campaign-per-step-table">
                      <thead>
                        <tr className="text-muted-foreground border-b border-theme">
                          <th className="text-left py-2 pr-2 font-medium">Step</th>
                          <th className="text-right py-2 px-1 font-medium">Sent</th>
                          <th className="text-right py-2 px-1 font-medium">Opened</th>
                          <th className="text-right py-2 px-1 font-medium">Clicked</th>
                          <th className="text-right py-2 px-1 font-medium">Replied</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.steps.map((s) => (
                          <tr key={s.step_index} className="border-b border-theme/50">
                            <td className="py-2 pr-2">
                              <span className="text-foreground font-medium">Step {s.step_index + 1}</span>
                              <span className="text-muted-foreground/70 ml-1.5 text-[10px] uppercase">{s.channel}</span>
                            </td>
                            <td className="py-2 px-1 text-right tabular-nums text-foreground">{s.sent}</td>
                            <td className="py-2 px-1 text-right tabular-nums text-blue-400">{s.opened}</td>
                            <td className="py-2 px-1 text-right tabular-nums text-purple-400">{s.clicked}</td>
                            <td className="py-2 px-1 text-right tabular-nums text-pink-400">{s.replied}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )
        ) : tab === "recipients" ? (
          (data.recipients || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No recipients yet — launch the campaign to populate this list.
            </p>
          ) : (
            <div className="divide-y divide-theme">
              {data.recipients.map((r) => (
                <div key={r.recipient_id || r.customer_id} className="flex items-center justify-between py-2 gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground text-sm font-medium truncate">{r.customer_name || "—"}</p>
                    <p className="text-muted-foreground text-[11px] truncate">{r.email || r.phone || ""}</p>
                  </div>
                  <Badge
                    className={`text-[10px] border ${
                      r.status === "sent" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                        : r.status === "failed" ? "bg-red-500/15 text-red-400 border-red-500/30"
                        : "bg-slate-500/15 text-slate-400 border-slate-500/30"
                    }`}
                    title={r.skipped_reason || ""}
                  >
                    {r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )
        ) : (
          (data.logs || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <div className="divide-y divide-theme">
              {data.logs.map((l) => (
                <div key={l.log_id} className="py-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{l.event}</Badge>
                    <Badge variant="outline" className="text-[10px]">{l.channel}</Badge>
                    <span className="text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-foreground mt-1">{l.customer_name || l.customer_id || ""}</p>
                  {l.body_preview && <p className="text-muted-foreground/70 mt-0.5 line-clamp-2">{l.body_preview}</p>}
                  {l.error && <p className="text-red-400 mt-0.5">Error: {l.error}</p>}
                </div>
              ))}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Analytics tab ────────────────────────────────────────────────

const AUDIENCE_LABEL = AUDIENCE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const STATUS_LABEL = { active: "Active", paused: "Paused", draft: "Draft", completed: "Completed" };

const KpiCard = ({ icon: Icon, label, value, sublabel, accent = "amber", testid }) => {
  const ACCENTS = {
    amber: "from-amber-500/15 to-amber-500/5 text-amber-400 border-amber-500/25",
    emerald: "from-emerald-500/15 to-emerald-500/5 text-emerald-400 border-emerald-500/25",
    blue: "from-blue-500/15 to-blue-500/5 text-blue-400 border-blue-500/25",
    purple: "from-purple-500/15 to-purple-500/5 text-purple-400 border-purple-500/25",
    pink: "from-pink-500/15 to-pink-500/5 text-pink-400 border-pink-500/25",
    teal: "from-teal-500/15 to-teal-500/5 text-teal-400 border-teal-500/25",
  };
  return (
    <Card className={`glass-card border bg-gradient-to-br ${ACCENTS[accent]}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium">
              {label}
            </p>
            <p className="text-2xl font-bold text-foreground font-['Manrope'] mt-1 truncate">
              {value}
            </p>
            {sublabel && (
              <p className="text-[11px] text-muted-foreground/70 mt-0.5">{sublabel}</p>
            )}
          </div>
          <Icon className="w-5 h-5 shrink-0 opacity-80" />
        </div>
      </CardContent>
    </Card>
  );
};

const FUNNEL_COLORS = ["#f59e0b", "#3b82f6", "#a855f7", "#ec4899", "#10b981", "#14b8a6"];

const FunnelChart = ({ funnel }) => {
  const data = [
    { label: "Sent", value: funnel.sent || 0 },
    { label: "Opened", value: funnel.opened || 0 },
    { label: "Clicked", value: funnel.clicked || 0 },
    { label: "Replied", value: funnel.replied || 0 },
    { label: "Converted", value: funnel.converted || 0 },
  ];
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2" data-testid="campaign-funnel-chart">
      {data.map((d, i) => {
        const pct = max > 0 ? Math.max((d.value / max) * 100, d.value > 0 ? 4 : 0) : 0;
        return (
          <div key={d.label} className="space-y-1">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-muted-foreground font-medium">{d.label}</span>
              <span className="text-foreground font-semibold tabular-nums">
                {d.value.toLocaleString()}
                {i > 0 && data[0].value > 0 && (
                  <span className="text-muted-foreground/60 ml-2 text-[10px]">
                    {((d.value / data[0].value) * 100).toFixed(1)}%
                  </span>
                )}
              </span>
            </div>
            <div className="h-3 rounded-full bg-theme-surface overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, backgroundColor: FUNNEL_COLORS[i] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AnalyticsTab = ({ onOpenCampaignAnalytics }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [sortBy, setSortBy] = useState("sent");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/analytics?days=${days}`, getAuth());
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="py-10 text-center" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-muted-foreground" />
      </div>
    );
  }

  const { totals, rates, campaigns: rows } = data;
  const sortedRows = [...rows].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  const fmtRevenue = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-5" data-testid="campaigns-analytics-tab">
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          Showing data from the last <span className="text-foreground font-medium">{data.window_days} days</span>
          {" · "}
          Conversions attributed within <span className="text-foreground font-medium">{data.attribution_days} days</span> of send.
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="analytics-days-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={Send} label="Sent" value={(totals.sent || 0).toLocaleString()}
          sublabel={`${(totals.failed || 0)} failed · ${(totals.skipped || 0)} skipped`}
          accent="amber" testid="kpi-sent"
        />
        <KpiCard
          icon={Mail} label="Open Rate" value={`${rates.open_rate || 0}%`}
          sublabel={`${(totals.opened || 0).toLocaleString()} unique`}
          accent="blue" testid="kpi-open-rate"
        />
        <KpiCard
          icon={MousePointerClick} label="Click Rate" value={`${rates.click_rate || 0}%`}
          sublabel={`${(totals.clicked || 0).toLocaleString()} unique`}
          accent="purple" testid="kpi-click-rate"
        />
        <KpiCard
          icon={Reply} label="Reply Rate" value={`${rates.reply_rate || 0}%`}
          sublabel={`${(totals.replied || 0).toLocaleString()} SMS replies`}
          accent="pink" testid="kpi-reply-rate"
        />
        <KpiCard
          icon={Target} label="Conversion" value={`${rates.conversion_rate || 0}%`}
          sublabel={`${(totals.converted || 0).toLocaleString()} jobs`}
          accent="emerald" testid="kpi-conversion-rate"
        />
        <KpiCard
          icon={DollarSign} label="Revenue" value={fmtRevenue(totals.revenue)}
          sublabel="From attributed jobs"
          accent="teal" testid="kpi-revenue"
        />
      </div>

      {/* Funnel + table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel */}
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <h3 className="text-foreground text-sm font-semibold font-['Manrope'] mb-3 flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-amber-500" /> Conversion Funnel
            </h3>
            <FunnelChart funnel={totals} />
          </CardContent>
        </Card>

        {/* Channel mix */}
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <h3 className="text-foreground text-sm font-semibold font-['Manrope'] mb-3 flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-amber-500" /> Event Mix
            </h3>
            <div className="h-48 min-h-[12rem] w-full" data-testid="analytics-event-bar">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: "Sent", value: totals.sent || 0 },
                    { name: "Opened", value: totals.opened || 0 },
                    { name: "Clicked", value: totals.clicked || 0 },
                    { name: "Replied", value: totals.replied || 0 },
                    { name: "Converted", value: totals.converted || 0 },
                  ]}
                  margin={{ top: 10, right: 8, bottom: 0, left: -16 }}
                >
                  <XAxis dataKey="name" stroke="#888" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#888" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(15, 15, 15, 0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {[0, 1, 2, 3, 4].map((i) => <Cell key={i} fill={FUNNEL_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-campaign table */}
      <Card className="glass-card border-theme">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="text-foreground text-sm font-semibold font-['Manrope'] flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-amber-500" /> Per-Campaign Breakdown
            </h3>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="analytics-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sent">Sort by Sent</SelectItem>
                <SelectItem value="opened">Sort by Opened</SelectItem>
                <SelectItem value="clicked">Sort by Clicked</SelectItem>
                <SelectItem value="replied">Sort by Replied</SelectItem>
                <SelectItem value="converted">Sort by Converted</SelectItem>
                <SelectItem value="revenue">Sort by Revenue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {sortedRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
              No campaigns yet — create one in the Overview tab.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="analytics-per-campaign-table">
                <thead>
                  <tr className="text-muted-foreground border-b border-theme">
                    <th className="text-left py-2 pr-2 font-medium">Campaign</th>
                    <th className="text-right py-2 px-1 font-medium">Sent</th>
                    <th className="text-right py-2 px-1 font-medium">Open %</th>
                    <th className="text-right py-2 px-1 font-medium">Click %</th>
                    <th className="text-right py-2 px-1 font-medium">Reply %</th>
                    <th className="text-right py-2 px-1 font-medium">Conv %</th>
                    <th className="text-right py-2 px-1 font-medium">Revenue</th>
                    <th className="text-right py-2 pl-1 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr
                      key={r.campaign_id}
                      className="border-b border-theme/50 hover:bg-theme-surface/30 transition-colors"
                      data-testid={`analytics-row-${r.campaign_id}`}
                    >
                      <td className="py-2 pr-2">
                        <div className="min-w-0">
                          <p className="text-foreground font-medium truncate max-w-[200px]">{r.name}</p>
                          <p className="text-muted-foreground/70 text-[10px] truncate">
                            {AUDIENCE_LABEL[r.audience_type] || r.audience_type} · {STATUS_LABEL[r.status] || r.status}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 px-1 text-right tabular-nums text-foreground">{r.sent.toLocaleString()}</td>
                      <td className="py-2 px-1 text-right tabular-nums">
                        <span className={r.open_rate > 0 ? "text-blue-400" : "text-muted-foreground/60"}>
                          {r.open_rate}%
                        </span>
                      </td>
                      <td className="py-2 px-1 text-right tabular-nums">
                        <span className={r.click_rate > 0 ? "text-purple-400" : "text-muted-foreground/60"}>
                          {r.click_rate}%
                        </span>
                      </td>
                      <td className="py-2 px-1 text-right tabular-nums">
                        <span className={r.reply_rate > 0 ? "text-pink-400" : "text-muted-foreground/60"}>
                          {r.reply_rate}%
                        </span>
                      </td>
                      <td className="py-2 px-1 text-right tabular-nums">
                        <span className={r.conversion_rate > 0 ? "text-emerald-400" : "text-muted-foreground/60"}>
                          {r.conversion_rate}%
                        </span>
                      </td>
                      <td className="py-2 px-1 text-right tabular-nums text-teal-400">
                        {fmtRevenue(r.revenue)}
                      </td>
                      <td className="py-2 pl-1 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={() => onOpenCampaignAnalytics(r)}
                          data-testid={`analytics-view-${r.campaign_id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};



// ─── Sender identity ──────────────────────────────────────────────

const STATUS_BADGE = {
  verified: { label: "Connected", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  pending: { label: "Pending verification", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  failed: { label: "Failed", color: "text-red-400 bg-red-500/10 border-red-500/30" },
};

const SenderIdentityCard = ({ identity, onConnect, onRefresh, onDisconnect, refreshing }) => {
  const status = identity?.status || "none";
  const badge = STATUS_BADGE[status];

  return (
    <Card className="glass-card border-theme" data-testid="sender-identity-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-amber-500" />
              <h3 className="text-foreground text-sm font-semibold font-['Manrope']">
                Campaign Sender Email
              </h3>
              {identity && badge && (
                <Badge
                  variant="outline"
                  className={`text-[10px] ${badge.color}`}
                  data-testid="sender-identity-status-badge"
                >
                  {badge.label}
                </Badge>
              )}
            </div>
            {!identity ? (
              <p className="text-sm text-muted-foreground" data-testid="sender-identity-empty-msg">
                Connect your sender email to start sending campaigns. We'll send a verification
                link to that inbox — no DNS records needed.
              </p>
            ) : (
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  <span className="font-medium" data-testid="sender-identity-email">
                    {identity.sender_email}
                  </span>
                  <span className="text-muted-foreground"> · {identity.sender_name}</span>
                </p>
                {status === "pending" && (
                  <p className="text-xs text-amber-400/90">
                    Check your inbox at <span className="font-medium">{identity.sender_email}</span> for
                    a verification link from MailerSend. Click it, then press <span className="font-medium">Refresh</span> below.
                  </p>
                )}
                {status === "failed" && identity.last_error && (
                  <p className="text-xs text-red-400/90">
                    {identity.last_error}
                  </p>
                )}
                {status === "verified" && (
                  <p className="text-xs text-emerald-400/80">
                    Campaign emails will go out from this address.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {identity && status === "pending" && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRefresh}
                disabled={refreshing}
                data-testid="sender-identity-refresh-btn"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh status"}
              </Button>
            )}
            {!identity ? (
              <Button
                size="sm"
                className="btn-primary"
                onClick={onConnect}
                data-testid="connect-sender-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Connect Sender Email
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onConnect}
                  data-testid="sender-identity-change-btn"
                >
                  Change
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                  onClick={onDisconnect}
                  data-testid="sender-identity-disconnect-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ConnectSenderDialog = ({ open, onClose, existing, onConnected }) => {
  const [form, setForm] = useState({ sender_email: "", sender_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        sender_email: existing?.sender_email || "",
        sender_name: existing?.sender_name || "",
      });
      setInfo(null);
    }
  }, [open, existing]);

  const submit = async () => {
    if (!form.sender_email.trim() || !form.sender_name.trim()) {
      toast.error("Both fields are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/sender-identities`,
        { sender_email: form.sender_email.trim(), sender_name: form.sender_name.trim() },
        getAuth(),
      );
      setInfo(res.data?.message || "Verification email sent.");
      toast.success("Verification email sent. Check your inbox.");
      onConnected(res.data?.identity);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect sender");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md"
        data-testid="connect-sender-dialog"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Connect Sender Email</DialogTitle>
          <DialogDescription>
            Campaigns will go out from this address. We'll email you a verification link — no DNS changes needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sender-email">Sender email</Label>
            <Input
              id="sender-email"
              type="email"
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              placeholder="you@yourcompany.com"
              data-testid="sender-email-input"
              autoComplete="off"
            />
            <p className="text-[10px] text-muted-foreground/70">
              Use your business domain. Free providers (Gmail, Yahoo, etc.) aren't supported.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender-name">From name</Label>
            <Input
              id="sender-name"
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="Acme Plumbing"
              data-testid="sender-name-input"
            />
          </div>
          {info && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-400/90">
              {info}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="sender-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="connect-sender-submit-btn" className="btn-primary">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {existing ? "Update" : "Send verification"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// ─── Page ──────────────────────────────────────────────────────────

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsCampaign, setLogsCampaign] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [identityDialogOpen, setIdentityDialogOpen] = useState(false);
  const [identityRefreshing, setIdentityRefreshing] = useState(false);

  const loadIdentity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sender-identities`, getAuth());
      setIdentity(res.data?.identity || null);
    } catch {
      // 404/network — treat as none
      setIdentity(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, tpl] = await Promise.all([
        axios.get(`${API}/campaigns`, getAuth()),
        axios.get(`${API}/campaigns/templates`, getAuth()),
      ]);
      setCampaigns(list.data.campaigns || []);
      setTemplates(tpl.data.templates || []);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); loadIdentity(); }, [refresh, loadIdentity]);

  const handleRefreshIdentity = async () => {
    setIdentityRefreshing(true);
    try {
      const res = await axios.post(`${API}/sender-identities/refresh`, {}, getAuth());
      setIdentity(res.data?.identity || null);
      if (res.data?.identity?.status === "verified") {
        toast.success("Sender email verified!");
      } else {
        toast.info("Still pending — click the link in your inbox first.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh");
    } finally {
      setIdentityRefreshing(false);
    }
  };

  const handleDisconnectIdentity = async () => {
    if (!window.confirm("Disconnect this sender email? Campaign sends will be paused until you connect another.")) return;
    try {
      await axios.delete(`${API}/sender-identities`, getAuth());
      setIdentity(null);
      toast.success("Sender email disconnected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleIdentityConnected = (newIdentity) => {
    setIdentity(newIdentity || null);
    // Keep dialog open so the user can read the "verification email sent" hint.
    // They'll close manually.
  };

  const handleLaunch = async (c) => {
    if (!window.confirm(`Launch "${c.name}" now? This will send to all matching customers.`)) return;
    setBusy(c.campaign_id);
    try {
      const res = await axios.post(`${API}/campaigns/${c.campaign_id}/launch`, {}, getAuth());
      const { sent, failed, skipped, total } = res.data;
      toast.success(`Launched ${c.name}: ${sent} sent, ${skipped} skipped, ${failed} failed (of ${total})`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Launch failed");
    } finally {
      setBusy(null);
    }
  };

  const handlePauseResume = async (c, active) => {
    try {
      await axios.put(`${API}/campaigns/${c.campaign_id}`, { is_active: active }, getAuth());
      toast.success(active ? "Activated" : "Paused");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/campaigns/${c.campaign_id}`, getAuth());
      toast.success("Campaign archived");
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  const onBuilderSaved = (saved, isNew) => {
    toast.success(isNew ? "Campaign created" : "Campaign updated");
    refresh();
  };

  const grouped = useMemo(() => {
    const map = { active: [], paused: [], draft: [], completed: [] };
    for (const c of campaigns) {
      const k = ["active", "paused", "draft", "completed"].includes(c.status) ? c.status : "draft";
      map[k].push(c);
    }
    return map;
  }, [campaigns]);

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope'] flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-amber-500" /> Marketing Campaigns
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Reach customers with targeted email + SMS — promotions, follow-ups, reactivations.
          </p>
        </div>
        <Button
          className="btn-primary"
          onClick={() => { setEditing(null); setBuilderOpen(true); }}
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Campaign
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full max-w-sm grid-cols-2" data-testid="campaigns-tabs">
          <TabsTrigger value="overview" data-testid="campaigns-tab-overview">
            <Inbox className="w-3.5 h-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="campaigns-tab-analytics">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-5">
          <SenderIdentityCard
            identity={identity}
            onConnect={() => setIdentityDialogOpen(true)}
            onRefresh={handleRefreshIdentity}
            onDisconnect={handleDisconnectIdentity}
            refreshing={identityRefreshing}
          />

          {/* Empty state OR sectioned list */}
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-6 h-6 animate-spin inline-block text-muted-foreground" />
            </div>
          ) : campaigns.length === 0 ? (
            <EmptyState onCreate={() => { setEditing(null); setBuilderOpen(true); }} />
          ) : (
            <div className="space-y-5">
              {["active", "paused", "draft", "completed"].map((k) => grouped[k].length > 0 && (
                <section key={k}>
                  <h2 className="text-foreground text-sm font-semibold capitalize mb-2 px-0.5 inline-flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" />
                    {k} <span className="text-muted-foreground font-normal">({grouped[k].length})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {grouped[k].map((c) => (
                      <CampaignCard
                        key={c.campaign_id}
                        c={c}
                        busy={busy === c.campaign_id}
                        onLaunch={handleLaunch}
                        onPause={(x) => handlePauseResume(x, false)}
                        onResume={(x) => handlePauseResume(x, true)}
                        onEdit={(x) => { setEditing(x); setBuilderOpen(true); }}
                        onLogs={(x) => { setLogsCampaign(x); setLogsOpen(true); }}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="mt-5">
          <AnalyticsTab
            onOpenCampaignAnalytics={(c) => { setLogsCampaign(c); setLogsOpen(true); }}
          />
        </TabsContent>
      </Tabs>

      <CampaignBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        templates={templates}
        onSaved={onBuilderSaved}
        editing={editing}
      />
      <LogsViewer
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        campaign={logsCampaign}
      />
      <ConnectSenderDialog
        open={identityDialogOpen}
        onClose={() => setIdentityDialogOpen(false)}
        existing={identity}
        onConnected={handleIdentityConnected}
      />
    </div>
  );
};

export default CampaignsPage;
